import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  CircularProgress,
  Alert,
  Button,
  Stack,
} from '@mui/material';
import Refresh from '@mui/icons-material/Refresh';
import PlayArrow from '@mui/icons-material/PlayArrow';
import {
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
} from 'recharts';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { getAllMonthlyKpiSnapshots, generateMonthlySummaryReport } from '../../../services/request/Reports';
import { MonthlyReport, MonthlySummaryResponse } from '../../../models/ReportInterface';
import { formatCurrency } from '../../../utils/CurrencyFormat';
import { PageHeader, EmptyState } from '../../../shared-components/ui';

function periodLabel(r: MonthlyReport): string {
  const m = r.monthName || `M${r.month}`;
  return `${m.slice(0, 3)} ${r.year}`;
}

const MonthlyKpiSnapshots: React.FC = () => {
  const [summary, setSummary] = useState<MonthlySummaryResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await getAllMonthlyKpiSnapshots();
      const payload = response.data;
      if (payload?.reports) {
        setSummary(payload);
      } else {
        setSummary(null);
      }
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Failed to load monthly KPI snapshots.');
      setSummary(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const rows = useMemo(() => {
    const list = summary?.reports ?? [];
    return [...list].sort((a, b) => {
      if (a.year !== b.year) return b.year - a.year;
      return b.month - a.month;
    });
  }, [summary]);

  const chartRows = useMemo(() => {
    const asc = [...(summary?.reports ?? [])].sort((a, b) => {
      if (a.year !== b.year) return a.year - b.year;
      return a.month - b.month;
    });
    return asc.map((r) => ({
      period: periodLabel(r),
      totalOutstanding: r.totalOutstanding ?? 0,
      interestCollected: r.interestCollected ?? 0,
      expectedMonthlyInterest: r.expectedMonthlyInterest ?? 0,
      monthlyNetCashflow: r.monthlyNetCashflow ?? 0,
      totalBorrowers: r.totalBorrowers ?? 0,
    }));
  }, [summary]);

  const handleGenerateNow = async () => {
    try {
      setGenerating(true);
      await generateMonthlySummaryReport();
      toast.success('Snapshot generated (or existing month returned).');
      await load();
    } catch (e: unknown) {
      toast.error(e instanceof Error ? e.message : 'Generate failed.');
    } finally {
      setGenerating(false);
    }
  };

  if (loading && !summary) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ maxWidth: 1400, mx: 'auto' }}>
      <ToastContainer position="top-right" autoClose={3000} />
      <PageHeader
        eyebrow="Lending · Reports"
        title="Monthly KPI snapshots"
        subtitle="End-of-month portfolio metrics captured with the monthly outstanding email batch."
        actions={
          <>
            <Button variant="outlined" startIcon={<Refresh />} onClick={() => void load()} disabled={loading}>
              Refresh
            </Button>
            <Button
              variant="contained"
              color="secondary"
              startIcon={<PlayArrow />}
              onClick={() => void handleGenerateNow()}
              disabled={generating}
            >
              Generate snapshot (now)
            </Button>
          </>
        }
      />

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {rows.length === 0 && !loading && (
        <EmptyState
          title="No snapshots yet"
          description='After the first month-end run, rows appear here. Use "Generate snapshot (now)" to capture the current month.'
        />
      )}

      {chartRows.length > 0 && (
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Trends (by month)
            </Typography>
            <Box sx={{ width: '100%', height: 380 }}>
              <ResponsiveContainer>
                <LineChart data={chartRows} margin={{ top: 8, right: 16, left: 8, bottom: 48 }}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="period" interval={0} angle={-28} textAnchor="end" height={70} tick={{ fontSize: 11 }} />
                  <YAxis tickFormatter={(v) => (v >= 100000 ? `${(v / 100000).toFixed(1)}L` : `${(v / 1000).toFixed(0)}k`)} />
                  <Tooltip
                    formatter={(value: number, name: string) => {
                      if (name === 'Borrowers') return [String(value), name];
                      return [formatCurrency(value), name];
                    }}
                  />
                  <Legend />
                  <Line type="monotone" dataKey="totalOutstanding" name="Total outstanding" stroke="#1565c0" strokeWidth={2} dot />
                  <Line type="monotone" dataKey="interestCollected" name="Interest earned (month)" stroke="#2e7d32" strokeWidth={2} dot />
                  <Line type="monotone" dataKey="expectedMonthlyInterest" name="Expected interest" stroke="#ef6c00" strokeWidth={2} dot />
                  <Line type="monotone" dataKey="monthlyNetCashflow" name="Monthly net cashflow" stroke="#6a1b9a" strokeWidth={2} dot />
                  <Line type="monotone" dataKey="totalBorrowers" name="Borrowers" stroke="#607d8b" strokeWidth={2} dot />
                </LineChart>
              </ResponsiveContainer>
            </Box>
          </CardContent>
        </Card>
      )}

      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Snapshot list
          </Typography>
          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Month</TableCell>
                  <TableCell align="right">Total outstanding</TableCell>
                  <TableCell align="right">Borrowers</TableCell>
                  <TableCell align="right">Interest earned (mo)</TableCell>
                  <TableCell align="right">Expected interest</TableCell>
                  <TableCell align="right">Collections (mo)</TableCell>
                  <TableCell align="right">Net cashflow</TableCell>
                  <TableCell align="right">New loans (mo)</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map((r) => (
                  <TableRow key={r.id}>
                    <TableCell>
                      {r.monthName} {r.year}
                    </TableCell>
                    <TableCell align="right">{formatCurrency(r.totalOutstanding)}</TableCell>
                    <TableCell align="right">{r.totalBorrowers}</TableCell>
                    <TableCell align="right">{formatCurrency(r.interestCollected)}</TableCell>
                    <TableCell align="right">{formatCurrency(r.expectedMonthlyInterest ?? 0)}</TableCell>
                    <TableCell align="right">{formatCurrency(r.totalCollections)}</TableCell>
                    <TableCell align="right">{formatCurrency(r.monthlyNetCashflow ?? 0)}</TableCell>
                    <TableCell align="right">{formatCurrency(r.newLoansAmount)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>
    </Box>
  );
};

export default MonthlyKpiSnapshots;
